import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { createZodFormResolver, showSuccessAlert, lt } from '@mews/framework';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { useCurrentRateGroupContext, useCurrentServiceIdContext } from '../../../../contexts';
import { createRateGroupDetailsSchema, toRateGroupUpdateParameters } from '../../../../schemas/rateGroupSchema';
import { useRateGroupsUpdate } from '../../../../mutations';
import { RateGroupFormDetailFields } from '../fields';

interface Props {
    initialLanguageCode: string;
}

export const useEditRateGroupDialogForm = ({ initialLanguageCode }: Props) =>
    useDialogForm({
        id: 'rateGroupEditForm',
        renderForm: (props) => <EditRateGroupForm {...props} initialLanguageCode={initialLanguageCode} />,
        title: lt('RateGroupEdit'),
    });

const EditRateGroupForm = ({ initialLanguageCode, ...props }: CommanderFormProps & Props) => {
    const { defaultLanguageCode } = useCurrentEnterprise();
    const currentRateGroup = useCurrentRateGroupContext();
    const serviceId = useCurrentServiceIdContext();

    const rateGroupDetailSchema = createRateGroupDetailsSchema({ defaultLanguageCode });
    const rateGroupDetailsFormResolver = createZodFormResolver(rateGroupDetailSchema);

    const { mutateAsync } = useRateGroupsUpdate();

    const { renderFormContent } = useHandledDialogFormControls({
        ...props,
        defaultValues: currentRateGroup,
        onSubmit: async (formValues) => {
            await mutateAsync(
                {
                    serviceId,
                    rateGroupUpdates: [
                        rateGroupDetailSchema
                            .transform(toRateGroupUpdateParameters({ rateGroupId: currentRateGroup.id }))
                            .parse(formValues),
                    ],
                },
                {
                    onSuccess: () => {
                        showSuccessAlert({ title: lt('RateGroupUpdatedSuccessMessage') });
                    },
                }
            );
        },
        resolver: rateGroupDetailsFormResolver,
    });

    return renderFormContent(
        <RateGroupFormDetailFields
            initialLanguageCode={initialLanguageCode}
            disableRateGroupKeyEditing={currentRateGroup.isPortfolioManaged}
        />
    );
};
